import { Radio } from '@cambridgeassessment/cambridge-ui';
import { Box, Typography } from '@material-ui/core';
import clsx from 'clsx';
import React, { memo, useCallback, useState } from 'react';
import useAuth from 'src/hooks/useAuth';
import useNotifications from 'src/hooks/useNotifications';
import { setSubscriptions } from 'src/services/gigya/gigya-service';
import { PreferenceRadioOption } from 'src/types';
import { DEFAULT_ERROR_MESSAGE } from '../../constants';
import PreferenceCard from '../PreferenceCard/PreferenceCard';
import { useStyles } from './styles';

type Props = {
  headTitle: string;
  headSubtitle?: string;
  bodyTitle?: string;
  bodySubtitle?: string;
  logo?: React.ReactNode;
  gigyaSubscriptionField: string;
};

const EmailSubscriptionRadio: React.FC<Props> = ({
  headTitle,
  headSubtitle,
  bodyTitle,
  bodySubtitle,
  logo,
  gigyaSubscriptionField,
}) => {
  const classes = useStyles();
  const { user, update } = useAuth();
  const { addNotification } = useNotifications();

  const [subsriptionStatus, setSubsriptionStatus] =
    useState<PreferenceRadioOption>(
      user.subscriptions?.[gigyaSubscriptionField]?.email?.isSubscribed
        ? PreferenceRadioOption.Yes
        : PreferenceRadioOption.No
    );

  const handleSubscriptionStatusChange = useCallback(
    (value: PreferenceRadioOption) => {
      setSubsriptionStatus(value);
      setSubscriptions(
        {
          [gigyaSubscriptionField]: {
            email: { isSubscribed: value === PreferenceRadioOption.Yes },
          },
        },
        (event) => {
          if (event.status === 'OK') {
            update();
            addNotification({ title: 'Subscription saved.' });
          } else {
            addNotification({
              variant: 'error',
              title: DEFAULT_ERROR_MESSAGE,
            });
          }
        }
      );
    },
    [addNotification, update, gigyaSubscriptionField]
  );

  return (
    <PreferenceCard title={headTitle} subtitle={headSubtitle} logo={logo}>
      <Box
        display="flex"
        justifyContent="space-between"
        className={classes.cardMobile}
      >
        <Box>
          <Box mb={1}>
            <Typography
              className={classes.newFont}
              variant="h5"
              data-testid="preference-body-title"
            >
              {bodyTitle}
            </Typography>
          </Box>
          <Typography
            className={classes.newFont}
            variant="body1"
            data-testid="preference-body-subtitle"
          >
            {bodySubtitle}
          </Typography>
        </Box>
        <Box display="inline-flex" pr={6} alignItems="center">
          <Box mr={4}>
            <Radio
              className={clsx(classes.radio)}
              dataTestId={`${gigyaSubscriptionField}_emails-Yes`}
              name={`${gigyaSubscriptionField} emails`}
              label={PreferenceRadioOption.Yes}
              onChange={() =>
                handleSubscriptionStatusChange(PreferenceRadioOption.Yes)
              }
              checked={subsriptionStatus === PreferenceRadioOption.Yes}
              bold
            />
          </Box>
          <Box mr={-2}>
            <Radio
              className={clsx(classes.radio)}
              dataTestId={`${gigyaSubscriptionField}_emails-No`}
              name={`${gigyaSubscriptionField} emails`}
              label={PreferenceRadioOption.No}
              onChange={() =>
                handleSubscriptionStatusChange(PreferenceRadioOption.No)
              }
              checked={subsriptionStatus === PreferenceRadioOption.No}
              bold
            />
          </Box>
        </Box>
      </Box>
    </PreferenceCard>
  );
};

export default memo(EmailSubscriptionRadio);
